/**
 * Custom hook to synchronize user data with Redux store
 */

import { useDispatch } from "react-redux";
import { useCallback } from "react";
import { hydrateInvestorAuth } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";

export interface UserDetail {
  id: number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  kycStatus?: string;
  isSuspended?: boolean;
  agreedToMembershipAgreement?: boolean;
  agreedToSpvMembershipAgreement?: boolean;
  orgName?: string;
  isAuthRep?: boolean;
  isAdminAuthRep?: boolean;
  entityType?: string;
  spvStatus?: string;
  spvName?: string;
  spvDetails?: any;
  pendingOnBoardingAs?: string;
  organization?: string;
  orgRoleId?: string;
  currentOrgRoleId?: string;
  regtankOnboardingFeatureFlag?: boolean;
  regtankLivenessFeatureFlag?: boolean;
  regtankLiveness?: any[];
  regtankOnboarding?: boolean;
  regtankRequest?: any[];
  declarationAISubmitted?: boolean;
  orgRole?: Array<{
    role?: { name?: string };
    creditLimit?: number;
  }>;
  hasEmailHistory?: boolean;
  hasSignedDocument?: boolean;
  adminOrgRoleId?: number | null;
}

/**
 * Hook to sync user data to Redux store
 * @returns Function to dispatch all user-related Redux actions
 */
export const useUserDataSync = () => {
  const dispatch = useDispatch();

  const syncUserData = useCallback(
    (userDetail: UserDetail) => {
      // Session storage
      sessionStorage.setItem("orgRoleId", userDetail.currentOrgRoleId || "");

      // Calculate derived values
      const hasCreditLimit = (userDetail?.orgRole?.[0]?.creditLimit || 0) > 0;

      // Single bulk dispatch replaces individual dispatches
      dispatch(
        hydrateInvestorAuth({
          kycStatus: userDetail?.kycStatus,
          isSuspended: userDetail?.isSuspended,
          agreedToMembershipAgreement: userDetail?.agreedToMembershipAgreement,
          orgName: userDetail?.orgName,
          agreedToSpvMembershipAgreement:
            userDetail?.agreedToSpvMembershipAgreement,
          hasCreditLimit,
          isAuthRep: userDetail?.isAuthRep,
          isAdminAuthRep: userDetail?.isAdminAuthRep,
          entityType: userDetail?.entityType,
          userId: userDetail?.id,
          userEmail: userDetail?.email,
          spvStatus: userDetail?.spvStatus,
          spvName: userDetail?.spvName,
          pendingOnBoardingAs: userDetail?.pendingOnBoardingAs,
          role: userDetail?.orgRole?.[0]?.role?.name,
          organization: userDetail?.organization,
          currentOrgRoleId: userDetail?.orgRoleId,
          firstName: userDetail?.firstName || null,
          lastName: userDetail?.lastName || null,
          spvDetails: userDetail?.spvDetails || null,
          regtankOnboardingFeatureFlag:
            userDetail?.regtankOnboardingFeatureFlag,
          regtankLivenessFeatureFlag: userDetail?.regtankLivenessFeatureFlag,
          regtankLiveness: userDetail?.regtankLiveness || null,
          regtankOnboarding: userDetail?.regtankOnboarding || false,
          regtankRequest: userDetail?.regtankRequest?.[0],
          declarationAISubmitted: userDetail?.declarationAISubmitted,
          hasEmailHistory: userDetail?.hasEmailHistory ?? false,
          hasSignedDocument: userDetail?.hasSignedDocument ?? false,
          adminOrgRoleId: userDetail?.adminOrgRoleId || null,
        })
      );
    },
    [dispatch]
  );

  return { syncUserData };
};
